import { Injectable } from '@angular/core';
import {
    GoEmissionDesignatorPowerType,
    GoEmissionDesignatorPowerTypeSortedArray,
    GoEmissionDesignatorPowerTypeToLabelMapping,
} from '../../../go-parameters/models/go-emission-designator-power-type';
import {
    GoEmissionDesignatorInformationType,
    GoEmissionDesignatorInformationTypeToLabelMapping,
} from '../../../go-parameters/models/go-emission-designator-information-type';
import {
    GoAntennaHeightUnitType,
    GoAntennaHeightUnitTypeToExpandedLabelMapping,
} from '../../../go-parameters/models/go-antenna-height-unit-type';
import {
    GoFrequencyType,
    GoFrequencyTypeToLabelMapping,
} from '../../../go-parameters/models/go-frequency-type';
import {
    GoLocationType,
    GoLocationTypeToLabelMapping,
} from '../../../go-parameters/models/go-location-type';
import {
    GoStationClassType,
    GoStationClassTypeToLabelMapping,
} from '../../../go-parameters/models/go-station-class-type';
import {
    GoDataDirectionType,
    GoDataDirectionTypeToLabelMapping,
} from '../../../go-parameters/models/go-data-direction-type';
import { GoApplicationPreparationService } from '../go-application-preparation/go-application-preparation.service';
import {
    GoErpWattsType,
    GoHeaderSpacingType,
} from './types/cevo-go-parameter-type';
import { GeoCoordinateSystemService } from '../../../../../../core/services/geo-coordinate-system/geo-coordinate-system.service';

@Injectable({
    providedIn: 'root',
})
export class GoApplicationSummaryService {
    constructor(
        private readonly goApplicationPreparationService: GoApplicationPreparationService,
        private readonly geoCoordinateSystemService: GeoCoordinateSystemService,
    ) {}

    getFrequencyTypeTitle(
        sectionHeading: GoHeaderSpacingType = 'heading',
    ): string {
        const space = this.getTitleSpaces(sectionHeading);
        return `1.${space}Band`;
    }

    getFrequencyTypeValueSummary(
        frequencyType: GoFrequencyType | null,
    ): string | null {
        if (frequencyType === null) {
            return null;
        }

        return GoFrequencyTypeToLabelMapping[frequencyType];
    }

    getLocationTypeTitle(
        sectionHeading: GoHeaderSpacingType = 'heading',
    ): string {
        const space = this.getTitleSpaces(sectionHeading);
        return `2.${space}Location Type`;
    }

    getLocationTypeValueSummary(
        locationType: GoLocationType | null,
    ): string | null {
        if (locationType === null) {
            return null;
        }

        return GoLocationTypeToLabelMapping[locationType];
    }

    getStationClassTitle(
        sectionHeading: GoHeaderSpacingType = 'heading',
    ): string {
        const space = this.getTitleSpaces(sectionHeading);
        return `3.${space}Station Class`;
    }

    getStationClassValueSummary(
        stationClass: GoStationClassType | null,
        dataDirection: GoDataDirectionType | null,
        separator: string | null,
    ): string | null {
        const stationClassTypeLabel = stationClass
            ? GoStationClassTypeToLabelMapping[stationClass]
            : null;

        const dataDirectionTypeLabel = dataDirection
            ? GoDataDirectionTypeToLabelMapping[dataDirection]
            : null;

        if (separator == null) {
            separator = ' | ';
        }

        return [stationClassTypeLabel, dataDirectionTypeLabel]
            .filter((value) => value !== null)
            .join(separator);
    }

    getNumberOfFrequenciesTitle(
        sectionHeading: GoHeaderSpacingType = 'heading',
    ): string {
        const space = this.getTitleSpaces(sectionHeading);
        return `4.${space}Number of Frequencies`;
    }

    getNumberOfFrequenciesValueSummary(
        numberOfFrequencies: number | null,
    ): string | null {
        if (numberOfFrequencies === null) {
            return null;
        }

        return numberOfFrequencies.toString();
    }

    getNumberOfFrequenciesSummaryPageText(
        numberOfFrequencies: number | null,
    ): string | null {
        if (numberOfFrequencies === null) {
            return null;
        }

        const pairText = numberOfFrequencies === 1 ? 'pair' : 'pairs';
        return `${numberOfFrequencies} ${pairText}`;
    }

    getCoordinatesTitle(
        sectionHeading: GoHeaderSpacingType = 'heading',
    ): string {
        const space = this.getTitleSpaces(sectionHeading);
        return `5.${space}Coordinates`;
    }

    getCoordinatesValueSummary(
        latitude: number | null,
        longitude: number | null,
        separator: string | null,
        asrNumber?: string,
    ): string | null {
        if (latitude === null || longitude === null) {
            return null;
        }

        const formattedLatitude =
            'Lat: ' +
            this.geoCoordinateSystemService.getFormattedDMS(
                Number(latitude),
                'latitude',
            );

        const formattedLongitude =
            'Long: ' +
            this.geoCoordinateSystemService.getFormattedDMS(
                Number(longitude),
                'longitude',
            );

        if (separator === null) {
            separator = ' / ';
        }

        let coordSummary = undefined;
        if (asrNumber) {
            coordSummary = `ASR: ${asrNumber}`;
        }

        return [coordSummary, formattedLatitude, formattedLongitude]
            .filter((x) => x !== undefined)
            .join(separator);
    }

    getCoordinatesValuePanelCollapsedAsr(asrNumber?: string): string | null {
        if (!asrNumber) {
            return null;
        }
        return `ASR: ${asrNumber}`;
    }

    getAntennaHeightTitle(
        sectionHeading: GoHeaderSpacingType = 'heading',
    ): string {
        const space = this.getTitleSpaces(sectionHeading);
        return `6.${space}Antenna Height`;
    }

    getAntennaHeightValueSummary(
        locationType: GoLocationType | null,
        antennaHeightUnitType: GoAntennaHeightUnitType | null,
        heightToTip: string | null,
    ): string | null {
        if (
            this.goApplicationPreparationService.isHeightToTipDisabled(
                locationType,
            )
        ) {
            return 'N/A, Mobile Only';
        }

        if (heightToTip === null || antennaHeightUnitType === null) {
            return null;
        }

        return `${heightToTip} ${GoAntennaHeightUnitTypeToExpandedLabelMapping[antennaHeightUnitType]}`;
    }

    getMinimumErpRequiredTitle(
        sectionHeading: GoHeaderSpacingType = 'heading',
    ): string {
        const space = this.getTitleSpaces(sectionHeading);
        return `7.${space}Minimum ERP Required`;
    }

    getMinimumErpRequiredValueSummary(
        fixedErp: string | null,
        mobileErp: string | null,
        separator: string | null,
        erpWattsType: GoErpWattsType = 'w',
    ): string | null {
        const fixedWatts = fixedErp
            ? `Fixed: ${fixedErp}${erpWattsType}`
            : null;
        const mobileWatts = mobileErp
            ? `Mobile: ${mobileErp}${erpWattsType}`
            : null;

        if (!fixedWatts && !mobileWatts) {
            return null;
        }

        if (separator == null) {
            separator = ', ';
        }

        return [fixedWatts, mobileWatts]
            .filter((x) => x !== null)
            .join(separator);
    }

    getEmissionDesignatorTitle(
        sectionHeading: GoHeaderSpacingType = 'heading',
    ): string {
        const space = this.getTitleSpaces(sectionHeading);
        return `8.${space}Emission Designator`;
    }

    getEmissionDesignatorValueSummary(
        powerSelected:
            | GoEmissionDesignatorPowerType
            | GoEmissionDesignatorPowerType[]
            | null,
        informationSelected: GoEmissionDesignatorInformationType | null,
    ): string | null {
        if (!powerSelected && !informationSelected) {
            return null;
        }

        const formattedPowersSelected =
            typeof powerSelected === 'string'
                ? [powerSelected]
                : powerSelected
                      ?.map((power) => {
                          return GoEmissionDesignatorPowerTypeToLabelMapping[
                              power
                          ];
                      })
                      .sort(
                          (a, b) =>
                              GoEmissionDesignatorPowerTypeSortedArray.indexOf(
                                  a,
                              ) -
                              GoEmissionDesignatorPowerTypeSortedArray.indexOf(
                                  b,
                              ),
                      )
                      .join(', ');

        const formattedInformationSelected = informationSelected
            ? GoEmissionDesignatorInformationTypeToLabelMapping[
                  informationSelected
              ]
            : null;

        return [formattedPowersSelected, formattedInformationSelected]
            .filter((x) => x !== null && x !== undefined)
            .join(' -&NoBreak;&nbsp;'); // Requirement: if the line would break, break before the dash
    }

    private getTitleSpaces(sectionHeading: GoHeaderSpacingType): string {
        return '&nbsp;'.repeat(sectionHeading == 'heading' ? 1 : 3);
    }
}
